/**
 * Cabeçalho + ficha do instrumento selecionado (M6 gráfico).
 * Identidade (barragem/seção/tipo), valor atual + tendência, nível, e a ficha
 * do cadastro (modo de leitura, cotas, profundidade, níveis de controle, carta).
 */
import { LevelIcon } from '@/ui';
import { NIVEL_EXIBIDO_UI } from '@/ui/tokens';
import { isInVigencia } from '@/domain/conformidade/anm';
import { ehNivelMedido, SEM_SINAL, type NivelExibido } from '@/domain/instrumentos/nivel-exibido';
import { ehBilateral, type FaixaCanal } from '@/domain/instrumentos/tilt';
import type { Instrument, ModoLeitura, Reading } from '@/domain';
import { TYPE_UNIT, type Trend } from '../domain/chart-data';
import { rotuloForaDeServico } from '@/domain/instrumentos/fora-de-servico';

const TREND_LABEL: Record<Trend, string> = {
  crescente: '↑ Crescente',
  decrescente: '↓ Decrescente',
  estavel: '→ Estável',
};

const MODO_LABEL: Record<ModoLeitura, string> = {
  automatico: 'Automático',
  manual: 'Manual',
};

/** Número em pt-BR (2 casas) + unidade; `—` quando nulo. */
function numBR(n: number | null, unit = ''): string {
  if (n == null) return '—';
  const s = n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return unit ? `${s} ${unit}` : s;
}

interface Props {
  instrument: Instrument;
  /** Rótulo da barragem (ex.: "Barragem B"); omitido quando ausente. */
  damLabel?: string;
  level: NivelExibido;
  current: Reading | undefined;
  trend: Trend;
  /** GUPM-270 — faixa vigente do canal (tiltímetro). `null` = instrumento escalar. */
  faixaCanal?: FaixaCanal | null;
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="truncate">
      <span className="text-jf-dim">{label}: </span>
      <span className="text-white">{value}</span>
    </div>
  );
}

export default function InstrumentDetail({
  instrument, damLabel, level, current, trend, faixaCanal = null,
}: Props) {
  const ui = NIVEL_EXIBIDO_UI[level];
  const unit = TYPE_UNIT[instrument.type] ?? '';
  const bilateral = ehBilateral(instrument.type);
  /**
   * GUPM-270 — para bilateral, "tem carta" é a faixa do CANAL, e a vigência é a dela.
   * `thresholds_source` não serve de prova aqui: em produção os 10 tiltímetros têm
   * `gpx-inventario-v2` nesse campo, que é o nome do inventário, não de uma Carta —
   * a ficha anunciaria uma Carta de Controle inexistente com a data do cadastro.
   */
  const temCarta = bilateral ? faixaCanal != null : instrument.thresholdsSource != null;
  /**
   * Bilateral: a Carta do tiltímetro está VIGENTE, e a ficha **não afirma desde
   * quando** — decisão do dono em 13/08/2026. O trecho recebido da Carta traz os
   * limiares e não traz a vigência; `valid_from` no banco é a 1ª leitura de cada TIL,
   * escolhida só para a classificação ter uma data com que comparar (R-A07). Exibi-la
   * como "vigente desde" creditaria à Carta uma data que ninguém informou. Volta a
   * aparecer quando as cartas completas chegarem, com a data real.
   */
  const vigente = bilateral ? temCarta : isInVigencia(instrument, new Date());
  /**
   * Limiar do canal bilateral vem em módulo (`±`); o escalar segue como estava.
   *
   * Bilateral NUNCA cai nos `level_*_max` do instrumento: são resíduo de seed e o
   * resíduo existe — o TIL-02 da Barragem B carrega 1/2/3 ° neles em produção, sem
   * nenhuma linha em `instrument_thresholds`. Sem faixa por canal a ficha mostra `—`
   * nos três campos: cego, não régua errada (ADR-0027).
   */
  const limiar = (doCanal: number | null | undefined, escalar: number | null) => {
    const v = bilateral ? doCanal ?? null : doCanal ?? escalar;
    if (v == null) return '—';
    return `${bilateral ? '±' : ''}${numBR(v, unit)}`;
  };
  const isCota = instrument.type === 'INA' || instrument.type === 'PZ';
  const validFromBR = instrument.thresholdsValidFrom.toLocaleDateString('pt-BR');
  /**
   * Rótulo da carta: o escalar credita fonte + vigência; o bilateral afirma só
   * "vigente" (ver acima). `filter(Boolean)` porque a fonte pode chegar nula — sem
   * ele, o literal "null" ia para a tela.
   */
  const cartaLabel = bilateral
    ? 'vigente'
    : [instrument.thresholdsSource, `vigente desde ${validFromBR}`].filter(Boolean).join(' · ');

  return (
    <>
      {temCarta && !vigente && (
        <div className="text-xs text-orange-300 mb-3">
          ⚠️ Carta de Controle {instrument.thresholdsSource} vige a partir de {validFromBR}. Leituras
          anteriores não são classificadas (R-A07).
        </div>
      )}

      <div className="flex justify-between items-start flex-wrap gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-base font-black text-white">{instrument.label}</div>
          <div className="text-xs text-jf-muted mt-0.5">
            {damLabel ? `${damLabel} · ` : ''}Seção {instrument.section ?? '—'} · Tipo: {instrument.type} · Unidade: {unit || '—'}
          </div>
          {/* GUPM-303 — instrumento mudo não tem "valor atual". O número continua na
              tela, porque esta é a página do instrumento e é aqui que o RT descobre em
              que condição o sensor parou (no TIL-05 a última leitura era coerente com
              as anteriores: caiu em repouso, não em movimento). O que muda é o rótulo,
              que passa a dizer o que o dado é, com o instante ao lado. Chamar de
              "atual" um número de dois dias atrás era a afirmação falsa. */}
          <div className="text-[13px] mt-1" style={{ color: ui.color }}>
            {level === SEM_SINAL ? 'Última leitura' : 'Valor atual'}:{' '}
            <strong className="font-mono">{current ? `${current.value} ${unit}` : '—'}</strong>
            {level === SEM_SINAL && current && (
              <span className="text-jf-muted">
                {' · '}
                {current.ts.toLocaleString('pt-BR', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            )}
            {'  |  '}
            Tendência: {TREND_LABEL[trend]}
          </div>
        </div>
        <span
          className="px-2 py-0.5 rounded-full text-[10px] font-black border inline-flex items-center gap-1"
          style={{ color: ui.color, borderColor: ui.border, background: ui.bg }}
        >
          {ehNivelMedido(level) && <LevelIcon level={level} size={12} />} {ui.label}
        </span>
      </div>

      {/* Ficha do instrumento (cadastro / Carta de Controle) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] border-t border-jf-border pt-2 mb-3">
        <Meta label="Modo de leitura" value={instrument.modoLeitura ? MODO_LABEL[instrument.modoLeitura] : '—'} />
        {/* GUPM-307 — tirar o instrumento dos níveis reais resolve o alarme falso e cria
            um risco novo: a ficha continua estampando o valor (798,58 m no INA-12A) sem
            dizer que ele é de antes da retirada. Valor velho sem etiqueta é pior que
            valor velho com alarme — o alarme ao menos avisava que havia algo ali.
            Ocupa as duas colunas restantes porque o motivo é texto livre do RT. */}
        {instrument.foraDeServicoDesde && (
          <div className="col-span-2 sm:col-span-2 truncate">
            <span className="text-jf-dim">
              {rotuloForaDeServico(instrument.foraDeServicoDesde)}
              {': '}
            </span>
            <span className="text-white">{instrument.foraDeServicoMotivo ?? '—'}</span>
          </div>
        )}
        {isCota && <Meta label="Cota de boca" value={numBR(instrument.cotaTopo, 'm')} />}
        {isCota && <Meta label="Cota de fundo" value={numBR(instrument.cotaFundo, 'm')} />}
        <Meta label="Profundidade" value={numBR(instrument.profundidade, 'm')} />
        {/* GUPM-270 — canal bilateral tem a régua em MÓDULO e por eixo: os campos
            legados `level*Max` são nulos para o tiltímetro, e mostrar "—" nas três
            linhas afirmaria que não há faixa quando ela existe (por canal). O `±`
            deixa explícito que o limiar vale nos dois sentidos do eixo. */}
        <Meta label="Atenção" value={limiar(faixaCanal?.inicioAtencao, instrument.levelAtencaoMax)} />
        <Meta label="Alerta" value={limiar(faixaCanal?.inicioAlerta, instrument.levelAlertaMax)} />
        <Meta label="Emergência" value={limiar(faixaCanal?.inicioEmergencia, instrument.levelEmergenciaMax)} />
        {temCarta && vigente && <Meta label="Carta de Controle" value={cartaLabel} />}
      </div>
    </>
  );
}
